import React, { useEffect, useRef, memo } from 'react';
import * as THREE from 'three';

export const PlayButton3D = memo(function PlayButton3D({ onClick, isLoading, isPlaying, isPKI = false }) {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const buttonGroupRef = useRef(null);
  const triangleRef = useRef(null);
  const pauseBarsRef = useRef(null);
  const isHoveredRef = useRef(false);
  const loadingRotationRef = useRef(0);
  const isLoadingRef = useRef(false);
  const isPlayingRef = useRef(false);
  const savedRotationRef = useRef(0);

  // Update refs when props change
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    if (!containerRef.current) return;

    // Capture ref for cleanup
    const container = containerRef.current;

    // Determine size based on screen width
    const getSize = () => {
      const width = window.innerWidth;
      if (width < 640) return 280; // mobile (sm)
      if (width < 1024) return 280; // tablet (md, lg)
      return 400; // desktop (xl+)
    };

    let size = getSize();

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(
      50,
      1, // aspect ratio 1:1 for square container
      0.1,
      1000
    );
    camera.position.z = 5;

    // Renderer
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true
    });
    renderer.setSize(size, size);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 1.2);
    directionalLight1.position.set(3, 3, 4);
    scene.add(directionalLight1);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.6);
    directionalLight2.position.set(-2, -2, -1);
    scene.add(directionalLight2);

    const directionalLight3 = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight3.position.set(0, -3, 2);
    scene.add(directionalLight3);

    // Create button group
    const buttonGroup = new THREE.Group();
    buttonGroupRef.current = buttonGroup;

    // Circle base (outer ring)
    const ringGeometry = new THREE.TorusGeometry(1.5, 0.1, 16, 64);
    const emissiveColor = isPKI ? 0xec4899 : 0x3b82f6; // Pink for PKI, Blue for KI
    const ringMaterial = new THREE.MeshStandardMaterial({
      color: emissiveColor,
      metalness: 0.3,
      roughness: 0.4,
      emissive: emissiveColor,
      emissiveIntensity: 0.6
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    buttonGroup.add(ring);

    // Inner circle removed - transparent background

    // Play triangle
    const triangleShape = new THREE.Shape();
    triangleShape.moveTo(0, 0.6);
    triangleShape.lineTo(0, -0.6);
    triangleShape.lineTo(1.0, 0);
    triangleShape.lineTo(0, 0.6);

    const extrudeSettings = {
      depth: 0.3,
      bevelEnabled: true,
      bevelThickness: 0.05,
      bevelSize: 0.05,
      bevelSegments: 3
    };

    const triangleGeometry = new THREE.ExtrudeGeometry(triangleShape, extrudeSettings);
    const triangleMaterial = new THREE.MeshStandardMaterial({
      color: emissiveColor,
      metalness: 0.3,
      roughness: 0.4,
      emissive: emissiveColor,
      emissiveIntensity: 0.6
    });
    const triangle = new THREE.Mesh(triangleGeometry, triangleMaterial);
    triangle.position.set(-0.3, 0, -0.2);
    triangleRef.current = triangle;
    buttonGroup.add(triangle);

    // Create pause bars (two vertical rectangles)
    const pauseGroup = new THREE.Group();

    const barWidth = 0.3;
    const barHeight = 1.2;
    const barDepth = 0.3;
    const barSpacing = 0.4;

    const barGeometry = new THREE.BoxGeometry(barWidth, barHeight, barDepth);
    const barMaterial = new THREE.MeshStandardMaterial({
      color: emissiveColor,
      metalness: 0.3,
      roughness: 0.4,
      emissive: emissiveColor,
      emissiveIntensity: 0.6
    });

    const bar1 = new THREE.Mesh(barGeometry, barMaterial);
    bar1.position.set(-barSpacing / 2, 0, 0);
    pauseGroup.add(bar1);

    const bar2 = new THREE.Mesh(barGeometry, barMaterial.clone());
    bar2.position.set(barSpacing / 2, 0, 0);
    pauseGroup.add(bar2);

    pauseGroup.visible = false; // Hidden by default
    pauseBarsRef.current = pauseGroup;
    buttonGroup.add(pauseGroup);

    scene.add(buttonGroup);

    // Animation loop
    let animationId;
    const animate = () => {
      animationId = requestAnimationFrame(animate);

      // Toggle play/pause symbol visibility
      if (triangleRef.current && pauseBarsRef.current) {
        triangleRef.current.visible = !isPlayingRef.current;
        pauseBarsRef.current.visible = isPlayingRef.current;
      }

      // Loading animation - faster rotation
      if (isLoadingRef.current) {
        loadingRotationRef.current += 0.05;
        buttonGroup.rotation.y = loadingRotationRef.current;
        // Pulse effect
        const pulse = Math.sin(Date.now() * 0.005) * 0.05 + 1;
        buttonGroup.scale.lerp(new THREE.Vector3(pulse, pulse, pulse), 0.1);
        ringMaterial.emissiveIntensity = THREE.MathUtils.lerp(
          ringMaterial.emissiveIntensity,
          0.9,
          0.1
        );
      } else {
        // Hover animation
        if (isHoveredRef.current) {
          // Normalize rotation to -PI to PI range for shortest path
          while (buttonGroup.rotation.y > Math.PI) buttonGroup.rotation.y -= Math.PI * 2;
          while (buttonGroup.rotation.y < -Math.PI) buttonGroup.rotation.y += Math.PI * 2;
          // Smoothly rotate to face forward (y = 0)
          buttonGroup.rotation.y = THREE.MathUtils.lerp(buttonGroup.rotation.y, 0, 0.03);

          buttonGroup.scale.lerp(new THREE.Vector3(1.1, 1.1, 1.1), 0.1);
          ringMaterial.emissiveIntensity = THREE.MathUtils.lerp(
            ringMaterial.emissiveIntensity,
            0.9,
            0.1
          );
          triangleMaterial.emissiveIntensity = THREE.MathUtils.lerp(
            triangleMaterial.emissiveIntensity,
            0.9,
            0.1
          );
        } else {
          // Continue rotation from saved position when not hovered
          savedRotationRef.current += 0.002;
          // Keep saved rotation within one full rotation (0 to 2π)
          if (savedRotationRef.current > Math.PI * 2) {
            savedRotationRef.current -= Math.PI * 2;
          }

          // Find shortest path to target
          let diff = savedRotationRef.current - buttonGroup.rotation.y;
          while (diff > Math.PI) diff -= Math.PI * 2;
          while (diff < -Math.PI) diff += Math.PI * 2;
          const target = buttonGroup.rotation.y + diff;

          buttonGroup.rotation.y = THREE.MathUtils.lerp(buttonGroup.rotation.y, target, 0.05);

          buttonGroup.scale.lerp(new THREE.Vector3(1, 1, 1), 0.1);
          ringMaterial.emissiveIntensity = THREE.MathUtils.lerp(
            ringMaterial.emissiveIntensity,
            0.6,
            0.1
          );
          triangleMaterial.emissiveIntensity = THREE.MathUtils.lerp(
            triangleMaterial.emissiveIntensity,
            0.6,
            0.1
          );
        }
      }

      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      const newSize = getSize();
      if (newSize !== size) {
        size = newSize;
        renderer.setSize(size, size);
        camera.updateProjectionMatrix();
      }
    };
    window.addEventListener('resize', handleResize);

    // Cleanup - properly dispose THREE.js resources to prevent memory leaks
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);

      // Dispose all geometries and materials
      scene.traverse((object) => {
        if (object.geometry) {
          object.geometry.dispose();
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose());
          } else {
            object.material.dispose();
          }
        }
      });

      if (container && renderer.domElement) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();

      // Clear refs
      sceneRef.current = null;
      rendererRef.current = null;
      buttonGroupRef.current = null;
      triangleRef.current = null;
      pauseBarsRef.current = null;
    };
  }, [isPKI]);

  const handleMouseEnter = () => {
    isHoveredRef.current = true;
  };

  const handleMouseLeave = () => {
    isHoveredRef.current = false;
  };

  return (
    <div
      ref={containerRef}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="w-[280px] h-[280px] sm:w-[280px] sm:h-[280px] lg:w-[400px] lg:h-[400px]"
      style={{
        cursor: 'pointer',
        pointerEvents: 'auto'
      }}
    />
  );
});
